/**
 * Parses Cube array format for standardTreeNames into array of arrays
 *
 * Format: {\"Standard Name\",Parent,Framework,NODES}, {\"Another\",Parent,Framework,NODES}
 *
 * Each tree goes from deepest (standard name) to topmost (NODES)
 *
 * @param standardTreeNames - Cube array string from Cube
 * @returns Array of tree arrays
 *
 * @example
 * Input: {\"Node #1\",English,ACT,NODES}, {\"Standard A.0.0.4\",\"Node #1\",English,ACT,NODES}
 * Output: [["Node #1", "English", "ACT", "NODES"], ["Standard A.0.0.4", "Node #1", "English", "ACT", "NODES"]]
 */
export const parseStandardTreeNames = (
  standardTreeNames: string | null
): string[][] => {
  if (!standardTreeNames) {
    return []
  }

  try {
    // Split by }, { pattern to get individual trees
    const treeStrings = standardTreeNames.split(/\},\s*\{/)

    return treeStrings.map(treeStr => {
      // Remove braces and unescape quotes
      const str = treeStr.replace(/[{}]/g, "").replace(/\\"/g, '"')

      // Split by comma, but not commas inside quotes
      // Match: either a quoted string OR a non-comma/non-quote sequence
      const matches = str.match(/"[^"]*"|[^,]+/g) || []

      return matches.map((element: string) =>
        element.replace(/^"|"$/g, "").trim()
      )
    })
  } catch (error) {
    console.error(
      "Failed to parse standardTreeNames:",
      error,
      standardTreeNames
    )
    return []
  }
}

/**
 * Extracts the framework name from a standard tree
 *
 * Pattern: Framework is always at position tree.length - 2 (second to last)
 * Last element is always "NODES"
 *
 * @param tree - Array of tree elements (deepest to topmost)
 * @returns Framework name (e.g., "ACT", "EACT") or null
 *
 * @example
 * Input: ["Node #1", "English", "ACT", "NODES"]
 * Output: "ACT"
 */
export const extractFrameworkFromTree = (tree: string[]): string | null => {
  if (tree.length < 2) {
    return null
  }

  // Framework is always second-to-last (before NODES)
  return tree[tree.length - 2]
}

/**
 * Groups standards by framework for a single content record
 *
 * @param standardTreeNames - Cube array string from Cube
 * @returns Object mapping framework names to arrays of standard names
 *
 * @example
 * Input: {\"Node #1\",English,ACT,NODES}, {\"Standard A.0.0.4\",\"Node #1\",English,ACT,NODES}
 * Output: { "ACT": ["Node #1", "Standard A.0.0.4"] }
 */
export const groupStandardsByFramework = (
  standardTreeNames: string | null
): Record<string, string[]> => {
  const trees = parseStandardTreeNames(standardTreeNames)
  const grouped: Record<string, string[]> = {}

  for (const tree of trees) {
    const framework = extractFrameworkFromTree(tree)
    const standardName = tree[0] // First element is the deepest level (the actual standard name)

    if (framework && standardName) {
      if (!grouped[framework]) {
        grouped[framework] = []
      }
      grouped[framework].push(standardName)
    }
  }

  return grouped
}
